import moment from "moment-timezone";
import {
  firestore,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from "@/firebase/init";
import store from "@/store/index.js";
import { logWorkflowWarning } from "@/plugins/sentry";

export function collectPendingPromotionCreditIds(order) {
  if (!order) return [];

  if (
    Array.isArray(order.pendingPromotionCredits) &&
    order.pendingPromotionCredits.length > 0
  ) {
    return [...order.pendingPromotionCredits];
  }

  if (order.pendingPromotionCredit) {
    return [order.pendingPromotionCredit];
  }

  return [];
}

export function hasActivePromotionCreditPayment(payments, creditId) {
  if (!creditId || !Array.isArray(payments)) return false;

  return payments.some(
    (payment) =>
      payment.paymentType === "promotionCredit" &&
      payment.promotionCredit === creditId &&
      !payment.flags?.reversed,
  );
}

export async function setPendingPromotionCredits(orderId, remainingCreditIds) {
  const remaining = Array.isArray(remainingCreditIds)
    ? remainingCreditIds.filter(Boolean)
    : [];

  await updateDoc(doc(firestore, "salesOrders", orderId), {
    pendingPromotionCredit: remaining[0] ?? null,
    pendingPromotionCredits: remaining,
    updated: serverTimestamp(),
    lastUpdated: {
      name: store.getters.getUser.name,
      uid: store.getters.getUser.uid,
    },
  });
}

export async function removeAppliedIdsFromPendingPromotionCredits(
  orderId,
  order,
  appliedCreditIds,
) {
  const applied = new Set(
    (Array.isArray(appliedCreditIds) ? appliedCreditIds : []).filter(Boolean),
  );
  if (applied.size === 0) return;

  const pending = collectPendingPromotionCreditIds(order);
  if (pending.length === 0) return;

  if (!pending.some((creditId) => applied.has(creditId))) return;

  const remaining = pending.filter((creditId) => !applied.has(creditId));
  await setPendingPromotionCredits(orderId, remaining);
}

/** Clear pending IDs that already have a non-reversed promo payment on the order. */
export async function syncPendingPromotionCreditsWithPayments(
  orderId,
  order,
  payments,
) {
  const pending = collectPendingPromotionCreditIds(order);
  if (pending.length === 0) return pending;

  const paidIds = pending.filter((creditId) =>
    hasActivePromotionCreditPayment(payments, creditId),
  );
  if (paidIds.length === 0) return pending;

  const remaining = pending.filter(
    (creditId) => !hasActivePromotionCreditPayment(payments, creditId),
  );
  await setPendingPromotionCredits(orderId, remaining);
  return remaining;
}

/**
 * Apply pending promotion credits as payments.
 * Clears pending for applied/skipped IDs; failures stay pending for retry.
 * Passes skipPendingClear into applyPayment so pending is written once at the end.
 */
export async function applyPendingPromotionCredits({
  salesOrderClient,
  creditIds,
  notes = "Credit applied on order approval",
} = {}) {
  if (!salesOrderClient?.orderID) {
    throw new Error("salesOrderClient with orderID is required");
  }

  await salesOrderClient.getOrder();
  await salesOrderClient.getPayments();

  const order = salesOrderClient.order;
  const attemptedIds =
    creditIds != null
      ? [...creditIds].filter(Boolean)
      : collectPendingPromotionCreditIds(order);

  const result = {
    attemptedIds,
    appliedIds: [],
    skippedIds: [],
    failedIds: [],
    remainingPendingIds: [],
    zeroBalance: false,
  };

  if (attemptedIds.length === 0) {
    return result;
  }

  // ?? so a legitimate $0 due does not fall through to total.
  let remainingOrderTotal = Number(
    parseFloat(order.balance?.due ?? order.balance?.total ?? 0).toFixed(2),
  );

  if (remainingOrderTotal <= 0) {
    result.zeroBalance = true;
    logWorkflowWarning(
      "applyPendingPromotionCredits: order balance is zero, leaving pending credits",
      {
        workflow: "sales-order",
        action: "pending-credits-zero-balance",
        context: {
          orderId: salesOrderClient.orderID,
          attemptedIds,
          balance: order.balance,
        },
      },
    );

    // Still classify already-paid IDs so sync can clear them; unpaid stay pending.
    await salesOrderClient.getPayments();
    for (const creditId of attemptedIds) {
      if (
        hasActivePromotionCreditPayment(salesOrderClient.payments, creditId)
      ) {
        result.skippedIds.push(creditId);
      } else {
        result.remainingPendingIds.push(creditId);
      }
    }
    await setPendingPromotionCredits(
      salesOrderClient.orderID,
      result.remainingPendingIds,
    );
    return result;
  }

  for (const creditId of attemptedIds) {
    if (remainingOrderTotal <= 0) {
      result.remainingPendingIds.push(creditId);
      continue;
    }

    try {
      // Fresh payments each iteration — avoids racing a parallel approve/recovery.
      await salesOrderClient.getPayments();

      if (
        hasActivePromotionCreditPayment(salesOrderClient.payments, creditId)
      ) {
        result.skippedIds.push(creditId);
        continue;
      }

      const creditSnap = await getDoc(
        doc(firestore, "promotionCredits", creditId),
      );

      if (!creditSnap.exists()) {
        // Keep pending — transient getDoc misses should be retryable.
        logWorkflowWarning("applyPendingPromotionCredits: credit missing", {
          workflow: "sales-order",
          action: "pending-credit-missing",
          context: { orderId: salesOrderClient.orderID, creditId },
        });
        result.failedIds.push(creditId);
        result.remainingPendingIds.push(creditId);
        continue;
      }

      const creditData = creditSnap.data();
      const currentAvailable = Number(
        parseFloat(creditData.available || 0).toFixed(2),
      );
      const isActive = creditData.active === true;

      if (!isActive || currentAvailable <= 0) {
        logWorkflowWarning("applyPendingPromotionCredits: credit stale", {
          workflow: "sales-order",
          action: "pending-credit-stale",
          context: {
            orderId: salesOrderClient.orderID,
            creditId,
            currentAvailable,
            isActive,
          },
        });
        result.skippedIds.push(creditId);
        continue;
      }

      const creditAmount = Math.min(currentAvailable, remainingOrderTotal);

      await salesOrderClient.applyPayment(
        {
          amount: creditAmount,
          checkNumber: "",
          date: moment().startOf("day").add(12, "hours").toDate(),
          notes,
          promotionCredit: creditId,
          type: {
            value: "promotionCredit",
            text: "Promotion Credit",
          },
        },
        { skipPendingClear: true },
      );

      await salesOrderClient.getOrder();
      await salesOrderClient.getPayments();

      if (
        !hasActivePromotionCreditPayment(salesOrderClient.payments, creditId)
      ) {
        logWorkflowWarning(
          "applyPendingPromotionCredits: applyPayment returned without creating payment",
          {
            workflow: "sales-order",
            action: "pending-credit-silent-skip",
            context: {
              orderId: salesOrderClient.orderID,
              creditId,
            },
          },
        );
        result.failedIds.push(creditId);
        result.remainingPendingIds.push(creditId);
        continue;
      }

      result.appliedIds.push(creditId);
      remainingOrderTotal = Number(
        parseFloat(
          salesOrderClient.order.balance?.due ??
            salesOrderClient.order.balance?.total ??
            0,
        ).toFixed(2),
      );
    } catch (error) {
      console.error(
        `applyPendingPromotionCredits: failed for ${creditId}:`,
        error,
      );
      logWorkflowWarning(
        `applyPendingPromotionCredits: error applying credit — ${error.message}`,
        {
          workflow: "sales-order",
          action: "pending-credit-apply-error",
          context: {
            orderId: salesOrderClient.orderID,
            creditId,
            balance: salesOrderClient.order?.balance,
          },
        },
      );
      result.failedIds.push(creditId);
      result.remainingPendingIds.push(creditId);
    }
  }

  await setPendingPromotionCredits(
    salesOrderClient.orderID,
    result.remainingPendingIds,
  );

  return result;
}
